import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Check, Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";
import type { Invoice } from "../../types/invoice";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";

type EditMode = "override" | "cancel";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: Invoice;
  onSuccess?: (result: { action: EditMode; invoice?: Invoice | null }) => void;
};

const currency = (n: number) =>
  `₹${Math.round(n).toLocaleString("en-IN")}`;

function roundCeil(n: number) {
  return Math.ceil(Number.isFinite(n) ? n : 0);
}

function getApiErrorMessage(error: unknown, fallback: string) {
  if (!error || typeof error !== "object") {
    return typeof error === "string" && error.trim() ? error : fallback;
  }
  const err = error as {
    error?: unknown;
    message?: unknown;
    data?: { error?: unknown; message?: unknown };
  };
  for (const value of [err.error, err.message, err.data?.error, err.data?.message]) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return fallback;
}

export function isInterimInvoice(invoice?: Invoice | null): boolean {
  if (!invoice) return false;
  if ((invoice as Invoice & { isInterim?: boolean }).isInterim) return true;
  const period = String(invoice.periodNo ?? "").trim().toLowerCase();
  if (period === "i") return true;
  const code = String(invoice.rsInvoiceCode ?? "").trim().toLowerCase();
  if (code.endsWith("-ri") || code.includes("-ri")) return true;
  return String(invoice.type ?? "").trim().toLowerCase() === "advance";
}

/** Interim edit allowed before IRN is generated, or after IRN is cancelled. Blocked if paid or IRN is still active. */
export function canEditInterimInvoice(invoice?: Invoice | null): boolean {
  if (!invoice || !isInterimInvoice(invoice)) return false;

  const totalPaid = Array.isArray(invoice.payments)
    ? invoice.payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0)
    : 0;
  if (totalPaid > 0) return false;

  const hasIrn = Boolean(String(invoice.externalIrn ?? "").trim());
  const status = String(invoice.externalInvoiceStatus ?? "")
    .trim()
    .toLowerCase();
  const failedReason = String(invoice.externalFailedReason ?? "").trim();
  const isIrnCancelled = status === "cancelled";
  const isIrnFailed =
    status === "failed" || status === "failed_notified" || failedReason !== "";

  // Active IRN → lock. Not generated / cancelled / failed → allow.
  if (hasIrn && !isIrnCancelled && !isIrnFailed) return false;

  return true;
}

export default function EditInterimRentalDialog({
  open,
  onOpenChange,
  invoice,
  onSuccess,
}: Props) {
  const [mode, setMode] = useState<EditMode>("override");
  const [baseAmount, setBaseAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const currentBase = Number(invoice.baseRounded ?? 0);
  const currentGst = Number(invoice.gstRounded ?? 0);
  const currentTds = Number(invoice.tdsRounded ?? 0);
  const currentTotal =
    Number(invoice.grandTotalRounded ?? 0) || currentBase + currentGst;

  const gstRate = useMemo(() => {
    const fromInvoice = Number(invoice.gstRate);
    if (Number.isFinite(fromInvoice) && fromInvoice > 0) return fromInvoice;
    if (currentBase > 0 && currentGst > 0) {
      return Math.round((currentGst / currentBase) * 100);
    }
    return 18;
  }, [invoice.gstRate, currentBase, currentGst]);

  const tdsRate = useMemo(() => {
    const fromInvoice = Number(invoice.tdsRate);
    if (Number.isFinite(fromInvoice) && fromInvoice >= 0) return fromInvoice;
    if (currentBase > 0 && currentTds > 0) {
      return Math.round((currentTds / currentBase) * 100);
    }
    return 2;
  }, [invoice.tdsRate, currentBase, currentTds]);

  useEffect(() => {
    if (!open) return;
    setMode("override");
    setBaseAmount(String(currentBase || ""));
    setRemarks("");
    setSubmitting(false);
  }, [open, currentBase, invoice.invoiceId]);

  const parsedBase = Number(baseAmount);
  const newBase = roundCeil(Number.isFinite(parsedBase) ? parsedBase : 0);
  const newGst = roundCeil(newBase * (gstRate / 100));
  const newTds = roundCeil(newBase * (tdsRate / 100));
  const newTotal = newBase + newGst;

  const canSubmitOverride =
    mode === "override" &&
    Number.isFinite(parsedBase) &&
    parsedBase > 0 &&
    newBase !== currentBase;

  const canSubmit = mode === "cancel" ? true : canSubmitOverride;

  const handleSubmit = async () => {
    if (!invoice.invoiceId) {
      toast.error("Invoice not found.");
      return;
    }
    if (!canSubmit) {
      if (mode === "override") {
        toast.error(
          newBase === currentBase
            ? "Enter a different base amount to override."
            : "Enter a valid base amount."
        );
      }
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiService.post<{
        success?: boolean;
        message?: string;
        error?: string;
        data?: Invoice & Record<string, unknown>;
      }>("/rental-schedule/edit-rental-schedule-interim-invoice", {
        invoiceId: invoice.invoiceId,
        action: mode,
        baseAmount: mode === "override" ? newBase : undefined,
        remarks: remarks.trim() || undefined,
      });

      if (response?.success === false) {
        toast.error(
          getApiErrorMessage(
            response,
            response.message || "Failed to update interim rental."
          )
        );
        return;
      }

      toast.success(
        response?.message ||
          (mode === "cancel"
            ? "Interim rental cancelled."
            : "Interim rental amount updated.")
      );
      onOpenChange(false);
      onSuccess?.({
        action: mode,
        invoice: (response?.data as Invoice) ?? null,
      });
    } catch (error) {
      console.error("Failed to edit interim rental:", error);
      toast.error(getApiErrorMessage(error, "Failed to update interim rental."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Interim Rental</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {invoice.rsInvoiceCode || invoice.displayId || invoice.invoiceId}
            {invoice.customer?.name ? ` · ${invoice.customer.name}` : ""}
          </p>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setMode("override")}
            className={cn(
              "rounded-lg border px-3 py-3 text-left transition-colors",
              mode === "override"
                ? "border-emerald-500 bg-emerald-50/60 ring-1 ring-emerald-500"
                : "border-border hover:bg-muted/40"
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">Override amount</span>
              {mode === "override" && (
                <Check className="h-4 w-4 text-emerald-600" />
              )}
            </div>
          </button>
          <button
            type="button"
            onClick={() => setMode("cancel")}
            className={cn(
              "rounded-lg border px-3 py-3 text-left transition-colors",
              mode === "cancel"
                ? "border-emerald-500 bg-emerald-50/60 ring-1 ring-emerald-500"
                : "border-border hover:bg-muted/40"
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">Cancel interim rental</span>
              {mode === "cancel" && (
                <Check className="h-4 w-4 text-emerald-600" />
              )}
            </div>
          </button>
        </div>

        {mode === "override" ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="interim-base-amount">New base amount (₹)</Label>
              <Input
                id="interim-base-amount"
                type="number"
                min={1}
                step="1"
                value={baseAmount}
                onChange={(e) => setBaseAmount(e.target.value)}
                className="min-w-0"
              />
            </div>

            <div className="overflow-hidden rounded-lg border">
              <div className="grid grid-cols-3 gap-2 border-b bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
                <span />
                <span className="text-right">Current</span>
                <span className="text-right">New</span>
              </div>
              {[
                { label: "Base", current: currentBase, next: newBase },
                { label: `GST (${gstRate}%)`, current: currentGst, next: newGst },
                {
                  label: `TDS (${tdsRate}%)`,
                  current: currentTds,
                  next: newTds,
                },
                { label: "Total", current: currentTotal, next: newTotal },
              ].map((row) => (
                <div
                  key={row.label}
                  className="grid grid-cols-3 gap-2 border-b px-3 py-2 text-sm last:border-b-0"
                >
                  <span className="font-medium">{row.label}</span>
                  <span className="text-right text-muted-foreground">
                    {currency(row.current)}
                  </span>
                  <span className="text-right font-medium">
                    {currency(row.next)}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              GST and TDS keep the invoice&apos;s existing rates. NoA present
              value is not recalculated.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800">
            This interim rental invoice ({currency(currentTotal)}) will be
            removed.
          </div>
        )}

        {/* <div className="space-y-2">
          <Label htmlFor="interim-remarks">Remarks (optional)</Label>
          <Textarea
            id="interim-remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Add any notes for this change…"
            rows={3}
            className="min-w-0"
          />
        </div> */}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Close
          </Button>
          <Button
            type="button"
            disabled={!canSubmit || submitting}
            onClick={() => void handleSubmit()}
            className={
              mode === "cancel"
                ? "bg-rose-500 text-white hover:bg-rose-600"
                : "bg-emerald-600 text-white hover:bg-emerald-700"
            }
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : mode === "cancel" ? (
              "Cancel Interim"
            ) : (
              "Submit"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
